import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import { EMPTY, catchError, switchMap, throwError } from "rxjs";
import { AuthField, AuthHead, AuthLayout } from "@app/components/auth-layout";
import { IcArrowLeft, IcKeyRound, IcMail, IcUser } from "@app/components/icons";
import NotificationModal from "@app/components/notification-modal";
import { addToast } from "@app/components/toast/toast.service";
import { Spinner, btn, btnGhost, btnLg, btnPrimary } from "@app/components/ui";
import {
  ACCESS_TOKEN_KEY,
  MAX_LEN,
  NotificationStatus,
  PathnameUrl,
  Role,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import { useT } from "@app/contexts/preferences.context";
import AccountService from "@app/services/http/account.service";
import AuthService from "@app/services/http/auth.service";
import NotificationService from "@app/services/http/notification.service";
import { openPortalDialog } from "@app/services/modal.service";
import { clearUser, storeUser } from "@app/store/auth/auth.action";
import { clearMyInfo, storeMyInfo } from "@app/store/my-info/my-info.action";
import { LoginInitialValues } from "@app/types";
import { loginValidationSchema } from "@app/validations";
import useBusy from "@core/hooks/use-busy.hook";
import StorageService from "@core/services/storage";

// API chỉ cho lấy OTP mới sau 2 phút (OTP cũng hết hạn sau 2 phút)
const OTP_WAIT = 120;

const leftFrom = (at: number) => Math.max(0, OTP_WAIT - Math.floor((Date.now() - at) / 1000));

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * Đăng nhập 2 bước như bản cũ: POST /auth/login-without-otp (gửi OTP về email) rồi POST /auth/login kèm OTP.
 * Bước OTP nằm ngay trong thẻ (bản cũ là modal không đóng được), có Quay lại và Gửi lại mã.
 */
function SignIn() {
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { busy, run } = useBusy();

  const [cred, setCred] = useState<LoginInitialValues | null>(null);
  // Bấm Quay lại thì điền sẵn tài khoản và mật khẩu vừa nhập
  const [last, setLast] = useState<LoginInitialValues>({ username: "", password: "" });
  const [otp, setOtp] = useState("");
  const [left, setLeft] = useState(0);
  const sentAt = useRef<{ key: string; at: number }>({ key: "", at: 0 });
  const otpRef = useRef<HTMLInputElement>(null);

  const secondsLeft = () => leftFrom(sentAt.current.at);

  useEffect(() => {
    if (!left) return;

    const timer = setTimeout(() => setLeft(leftFrom(sentAt.current.at)), 1000);

    return () => clearTimeout(timer);
  }, [left]);

  useEffect(() => {
    if (cred) otpRef.current?.focus();
  }, [cred]);

  const toOtp = (values: LoginInitialValues) => {
    setCred(values);
    setLast(values);
    setOtp("");
    setLeft(secondsLeft());
  };

  const sendOtp = (values: LoginInitialValues) => {
    const key = `${values.username.trim().toLowerCase()}\n${values.password}`;

    // Vừa nhận mã cho đúng tài khoản này (bấm Quay lại rồi đăng nhập lại): dùng tiếp mã cũ, API chưa cho lấy mã mới
    if (sentAt.current.key === key && secondsLeft() > 0) {
      toOtp(values);

      return;
    }

    run("login", () => AuthService.loginWithoutOtp(values.username, values.password), (res) => {
      if (res?.statusCode === 200) {
        sentAt.current = { key, at: Date.now() };
        toOtp(values);
      }
    });
  };

  const resend = () => {
    if (!cred || secondsLeft() > 0 || busy.resend) return;

    run("resend", () => AuthService.loginWithoutOtp(cred.username, cred.password), (res) => {
      if (res?.statusCode === 200) {
        sentAt.current = { ...sentAt.current, at: Date.now() };
        setLeft(secondsLeft());
        setOtp("");
        addToast({ text: t("Đã gửi mã OTP mới về email") });
      }
    });
  };

  // Giống OtpModal cũ: lưu token, hiện thông báo của admin (nếu có), tải thông tin tài khoản rồi vào trang thuê
  const verify = (e: React.FormEvent) => {
    e.preventDefault();

    if (!cred || otp.length !== 6 || busy.otp) return;

    run(
      "otp",
      () =>
        AuthService.login(cred.username, cred.password, otp).pipe(
          switchMap((loginRes) => {
            if (loginRes.role !== Role.USER) {
              addToast({ text: SystemMessage.DO_NOT_PERMISSION, status: "inValid" });

              return EMPTY;
            }

            StorageService.set(ACCESS_TOKEN_KEY, loginRes.jwt);
            StorageService.setObject(USER_INFO_KEY, { _id: loginRes._id, username: loginRes.username });
            dispatch(storeUser(loginRes));

            return NotificationService.getNotification().pipe(
              switchMap((notificationRes) => {
                if (notificationRes?.status === NotificationStatus.SHOW) {
                  openPortalDialog(NotificationModal, {
                    title: notificationRes.title,
                    content: notificationRes.content,
                  });
                }

                return AccountService.getMyInfo().pipe(
                  catchError((err) => {
                    dispatch(clearUser());
                    dispatch(clearMyInfo());

                    return throwError(() => err);
                  })
                );
              })
            );
          })
        ),
      (data) => {
        dispatch(storeMyInfo(data));
        navigate(PathnameUrl.GPU_HUB);
      }
    );
  };

  if (cred) {
    return (
      <AuthLayout>
        <button
          type="button"
          className="-ml-1 mb-4 inline-flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-[13px] font-semibold text-muted hover:text-fg"
          onClick={() => setCred(null)}
        >
          <IcArrowLeft className="h-4 w-4" />
          {t("Quay lại")}
        </button>
        <AuthHead title={t("Nhập mã OTP")} sub={t("Mã 6 số vừa được gửi tới email đăng ký của tài khoản.")} />

        <form className="flex flex-col gap-4" onSubmit={verify}>
          <div className="flex items-center gap-2.5 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px]">
            <IcUser className="h-[17px] w-[17px] flex-none text-faint" />
            <span className="text-muted">{t("Tài khoản")}</span>
            <b className="ml-auto truncate font-mono text-[12.5px]">{cred.username.trim()}</b>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-muted">{t("Mã OTP")}</span>
            <input
              ref={otpRef}
              className="h-12 w-full rounded-[10px] border border-line bg-surface px-3 text-center font-mono text-[20px] font-semibold tracking-[.4em] text-fg outline-none placeholder:text-faint focus:border-brand focus:shadow-[0_0_0_3px_var(--brand-soft)]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="••••••"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </label>

          <p className="m-0 flex items-start gap-2 rounded-[10px] bg-warn-soft px-3 py-2.5 text-[12.5px] font-medium text-warn">
            <IcMail className="mt-px h-4 w-4 flex-none" />
            {t("Mã có hiệu lực trong 2 phút. Không thấy email? Hãy xem mục Thư rác hoặc Quảng cáo.")}
          </p>

          <button
            type="submit"
            className={clsx(btn, btnPrimary, btnLg, "w-full")}
            disabled={otp.length !== 6 || busy.otp}
          >
            {busy.otp && <Spinner />}
            {t("Xác nhận và đăng nhập")}
          </button>
          <button
            type="button"
            className={clsx(btn, btnGhost, "w-full")}
            disabled={left > 0 || busy.resend}
            onClick={resend}
          >
            {busy.resend && <Spinner />}
            {left > 0 ? `${t("Gửi lại mã sau")} ${mmss(left)}` : t("Gửi lại mã")}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHead title={t("Đăng nhập")} sub={t("Chào mừng bạn quay lại PC Render.")} />

      <Formik
        initialValues={last}
        onSubmit={sendOtp}
        validationSchema={loginValidationSchema}
      >
        <Form className="flex flex-col gap-4" noValidate>
          <AuthField
            name="username"
            label={t("Tên tài khoản")}
            icon={<IcUser />}
            placeholder={t("Nhập tên tài khoản")}
            maxLength={MAX_LEN.CREDENTIAL}
            autoComplete="username"
            autoFocus
          />
          <AuthField
            name="password"
            label={t("Mật khẩu")}
            icon={<IcKeyRound />}
            type="password"
            placeholder={t("Nhập mật khẩu")}
            maxLength={MAX_LEN.CREDENTIAL}
            autoComplete="current-password"
          />
          <Link
            className="-mt-1 self-end text-[13px] font-semibold text-brand hover:underline"
            to={PathnameUrl.CONFIRM_ACCOUNT}
          >
            {t("Quên mật khẩu?")}
          </Link>
          <button type="submit" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy.login}>
            {busy.login && <Spinner />}
            {t("Đăng nhập")}
          </button>
        </Form>
      </Formik>

      <p className="mb-0 mt-6 border-t border-line pt-5 text-center text-[13.5px] text-muted">
        {t("Bạn chưa có tài khoản?")}{" "}
        <Link className="font-semibold text-brand hover:underline" to="/register">
          {t("Đăng ký ngay")}
        </Link>
      </p>
    </AuthLayout>
  );
}

export default SignIn;
